import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Edit2, Users, FolderKanban, Building2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

interface Organization {
  id: string;
  name: string;
  _count?: {
    users: number;
    projects: number;
    customers: number;
  };
}

interface UserSummary {
  id: string;
  name: string | null;
  email: string;
  userRole: string;
  organizationId: string | null;
}

const Organizations = () => {
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [editOrgName, setEditOrgName] = useState("");

  const { data: organizations = [], isLoading: organizationsLoading } = useQuery<Organization[]>({
    queryKey: ["organizations"],
    queryFn: () => api.get("/organizations"),
  });

  const { data: users = [], isLoading: usersLoading } = useQuery<UserSummary[]>({
    queryKey: ["users"],
    queryFn: () => api.get("/users"),
  });

  const createOrganization = useMutation({
    mutationFn: (payload: { name: string }) => api.post("/organizations", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      setNewOrgName("");
      setIsCreateDialogOpen(false);
      toast.success("Organization created");
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to create organization");
    },
  });

  const updateOrganization = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.patch(`/organizations/${id}`, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success("Organization updated");
      setEditingOrg(null);
      setEditOrgName("");
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to update organization");
    },
  });

  const assignUserToOrg = useMutation({
    mutationFn: ({ userId, organizationId }: { userId: string; organizationId: string | null }) =>
      api.patch(`/users/${userId}`, { organizationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success("User updated");
    },
    onError: () => {
      toast.error("Failed to update user");
    },
  });

  const organizationLookup = useMemo(() => {
    const map = new Map<string, Organization>();
    organizations.forEach((org) => map.set(org.id, org));
    return map;
  }, [organizations]);

  const handleCreateOrganization = () => {
    if (!newOrgName.trim()) {
      toast.error("Organization name is required");
      return;
    }
    createOrganization.mutate({ name: newOrgName.trim() });
  };

  const handleUpdateOrganization = () => {
    if (!editingOrg) return;
    if (!editOrgName.trim()) {
      toast.error("Organization name is required");
      return;
    }
    updateOrganization.mutate({ id: editingOrg.id, name: editOrgName.trim() });
  };

  const handleAssignUser = (userId: string, value: string) => {
    const nextOrg = value === "__none__" ? null : value;
    assignUserToOrg.mutate({ userId, organizationId: nextOrg });
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Organizations</h1>
          <p className="text-muted-foreground">
            Manage organizations and assign users. Only SuperAdmins can access this area.
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Organization
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {organizationsLoading
          ? Array.from({ length: 3 }).map((_, idx) => (
              <Card key={idx} className="border-dashed">
                <CardContent className="py-10 flex items-center justify-center text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading organizations...
                  </div>
                </CardContent>
              </Card>
            ))
          : organizations.length > 0
          ? organizations.map((org) => (
              <Card key={org.id}>
                <CardHeader className="flex flex-row items-start justify-between space-y-0">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-primary" />
                      {org.name}
                    </CardTitle>
                    <CardDescription>{org._count?.users ?? 0} users / {org._count?.projects ?? 0} projects</CardDescription>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      setEditingOrg(org);
                      setEditOrgName(org.name);
                    }}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{org._count?.users ?? 0} members</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <FolderKanban className="h-4 w-4" />
                    <span>{org._count?.projects ?? 0} projects</span>
                  </div>
                </CardContent>
              </Card>
            ))
          : (
            <Card className="col-span-full">
              <CardContent className="py-10 text-center space-y-2">
                <p className="text-lg font-medium">No organizations yet</p>
                <p className="text-muted-foreground text-sm">Create your first organization to get started.</p>
              </CardContent>
            </Card>
          )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>Assign users to organizations.</CardDescription>
        </CardHeader>
        <CardContent>
          {usersLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading users...
            </div>
          ) : users.length === 0 ? (
            <p className="text-muted-foreground">No users found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4">User</th>
                    <th className="py-2 pr-4">Role</th>
                    <th className="py-2 pr-4">Organization</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b last:border-0">
                      <td className="py-3 pr-4">
                        <div className="flex flex-col">
                          <span className="font-medium">{user.name || "No name"}</span>
                          <span className="text-xs text-muted-foreground">{user.email}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant="outline">{user.userRole}</Badge>
                      </td>
                      <td className="py-3 pr-4">
                        <Select
                          value={user.organizationId || "__none__"}
                          onValueChange={(value) => handleAssignUser(user.id, value)}
                          disabled={assignUserToOrg.isPending}
                        >
                          <SelectTrigger className="w-[220px]">
                            <SelectValue placeholder="Select organization" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Unassigned</SelectItem>
                            {organizations.map((org) => (
                              <SelectItem key={org.id} value={org.id}>
                                {org.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create organization</DialogTitle>
            <DialogDescription>Provide a name for the new organization.</DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Organization name"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateOrganization} disabled={createOrganization.isPending}>
              {createOrganization.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingOrg} onOpenChange={(open) => {
        if (!open) {
          setEditingOrg(null);
          setEditOrgName("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit organization</DialogTitle>
            <DialogDescription>Rename the selected organization.</DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Organization name"
            value={editOrgName}
            onChange={(e) => setEditOrgName(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEditingOrg(null);
              setEditOrgName("");
            }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateOrganization} disabled={updateOrganization.isPending}>
              {updateOrganization.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Organizations;

